/**
 * @fileoverview Hook for fetching user engagement stats
 * 
 * Returns the user's total points, current level, and progress to next level.
 * Uses the 5-level Status system: Newcomer → Explorer → Trendsetter → Fashionista → Style Icon
 * 
 * ANTI-GAMING DESIGN:
 * Points are awarded via database function with daily caps:
 * - Session login: 3 pts/day (once only)
 * - Saves: 1 pt each, max 5/day
 * - Votes: 1 pt each, max 5/day  
 * - Collections: 2 pts each, max 2/day
 * - Weekly streak bonus: 10 pts every 7 consecutive days
 * 
 * Max theoretical daily: ~17 pts
 * Max theoretical weekly: ~129 pts (with streak bonus)
 * 
 * Level thresholds designed for ~6 months to reach Style Icon with consistent engagement:
 * - Realistic weekly avg for active user: ~50-70 pts
 * - 6 months ≈ 26 weeks ≈ 1,300-1,800 pts
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface StatusLevel {
  level: number;
  name: string;
  minPoints: number;
  maxPoints: number;
  weeksToReach: string; // Approximate time for active users
}

export const STATUS_LEVELS: StatusLevel[] = [
  { level: 1, name: 'Newcomer', minPoints: 0, maxPoints: 49, weeksToReach: '0' },
  { level: 2, name: 'Explorer', minPoints: 50, maxPoints: 199, weeksToReach: '~1 week' },
  { level: 3, name: 'Trendsetter', minPoints: 200, maxPoints: 599, weeksToReach: '~1 month' },
  { level: 4, name: 'Fashionista', minPoints: 600, maxPoints: 1499, weeksToReach: '~3 months' },
  { level: 5, name: 'Style Icon', minPoints: 1500, maxPoints: Infinity, weeksToReach: '~6 months' },
];

export function getStatusLevel(points: number): StatusLevel {
  for (let i = STATUS_LEVELS.length - 1; i >= 0; i--) {
    if (points >= STATUS_LEVELS[i].minPoints) {
      return STATUS_LEVELS[i];
    }
  }
  return STATUS_LEVELS[0];
}

export function getProgressToNextLevel(points: number): number {
  const currentLevel = getStatusLevel(points);
  if (currentLevel.level === 5) return 100; // Max level
  
  const nextLevel = STATUS_LEVELS[currentLevel.level];
  const pointsInCurrentLevel = points - currentLevel.minPoints;
  const pointsNeededForNextLevel = nextLevel.minPoints - currentLevel.minPoints;
  
  return Math.min(100, Math.round((pointsInCurrentLevel / pointsNeededForNextLevel) * 100));
}

export interface EngagementStats {
  totalPoints: number;
  currentStreak: number;
  longestStreak: number;
  currentLevel: StatusLevel;
  progressToNextLevel: number;
  pointsToNextLevel: number;
}

export function useEngagementStats(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  return useQuery({
    queryKey: ['engagement-stats', targetUserId],
    queryFn: async (): Promise<EngagementStats | null> => {
      if (!targetUserId) return null;

      const { data, error } = await supabase
        .from('user_engagement_stats')
        .select('total_points, current_streak_days, longest_streak_days')
        .eq('user_id', targetUserId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching engagement stats:', error);
        return null;
      }

      const totalPoints = data?.total_points ?? 0;
      const currentLevel = getStatusLevel(totalPoints);
      const progressToNextLevel = getProgressToNextLevel(totalPoints);
      
      // Calculate points needed for next level
      let pointsToNextLevel = 0;
      if (currentLevel.level < 5) {
        const nextLevel = STATUS_LEVELS[currentLevel.level];
        pointsToNextLevel = nextLevel.minPoints - totalPoints;
      }

      return {
        totalPoints,
        currentStreak: data?.current_streak_days ?? 0,
        longestStreak: data?.longest_streak_days ?? 0,
        currentLevel,
        progressToNextLevel,
        pointsToNextLevel,
      };
    },
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
